// src/pages/Contact.tsx
import React from 'react';
import { Mail, BriefcaseIcon, Hand } from 'lucide-react';
import { PAGES, usePageMeta } from '../seo/seo';

const ContactPage = () => {
  usePageMeta(PAGES.contact);
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#3399FF] to-[#36AC98] text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              Have questions? We're here to help. Reach out to our team for support,
              partnership opportunities, or career inquiries.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Cards */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8">
          <ContactCard
            icon={<Mail />}
            title="Support"
            description="Need help with our products or services? Our support team is ready to assist you."
            email="support@cognitaix.com"
            buttonText="Get Support"
          />
          
          <ContactCard
            icon={<Hand />}
            title="Partnerships"
            description="Interested in partnering with us? Let's explore opportunities together."
            email="partnerships@cognitaix.com"
            buttonText="Discuss Partnership"
          />
          
          <ContactCard
            icon={<BriefcaseIcon />}
            title="Careers"
            description="Join our team and help shape the future of digital development."
            email="careers@cognitaix.com"
            buttonText="View Opportunities"
            linkedInUrl="https://www.linkedin.com/company/cognitaix"
          />
        </div>
      </div>
    </div>
  );
};

interface ContactCardProps {
  icon: React.ReactElement;
  title: string;
  description: string;
  email: string;
  buttonText: string;
  linkedInUrl?: string;
}

const ContactCard: React.FC<ContactCardProps> = ({
  icon,
  title,
  description,
  email,
  buttonText,
  linkedInUrl
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
      <div className="text-[#3399FF] mb-4">
        {React.cloneElement(icon as React.ReactElement<any>, { width: 32, height: 32 })}
      </div>
      <h3 className="text-xl font-semibold mb-2 dark:text-white">{title}</h3>
      <p className="text-gray-600 dark:text-gray-300 mb-4">{description}</p>
      <a
        href={`mailto:${email}`}
        className="text-[#36AC98] hover:text-[#3399FF] transition-colors mb-4 block"
      >
        {email}
      </a>
      <div className="mt-4">
        <button className="w-full bg-[#3399FF] text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors">
          {buttonText}
        </button>
        {linkedInUrl && (
          <a
            href={linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 block text-center text-[#36AC98] hover:text-[#3399FF] transition-colors"
          >
            View on LinkedIn
          </a>
        )}
      </div>
    </div>
  );
};

export default ContactPage;